import { AddonSelectionSheet } from '@components/BottomSheet/AddonSelectionSheet';
import { ProductAddonListSheet } from '@components/BottomSheet/ProductAddonListSheet';
import { ButcherHeader } from '@components/ButcherHeader';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderStepper } from '@components/OrderCreation/OrderStepper';
import { useToast } from '@contexts/Toast';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  OrderCreationMasterData,
  ProductType,
  WeightType,
  errorMessage,
  orderBreadCrumbSteps,
  orderStepper,
  responseTexts,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
  MasterProduct,
  ModifiedSelectedProduct,
  NewOrderInitiationProps,
  ProductDetails,
  SelectedProduct,
} from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { searchService } from '@services/OrderCreationService';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const OrderProductSelection: React.FC = () => {
  const [currentCustomizingId, setCurrentCustomizingId] = useState<string>('');
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  const [deletion, setDeletion] = useState(false);
  const [searchKey, setsearchKey] = useState<string>('');
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<MasterProduct[]>([]);
  const [customerId, setCustomerId] = useState<number>(0);
  const [addonSelectionSheet, setAddonSelectionSheet] = useState(false);
  const [productAddonListSheet, setProductAddonListSheet] = useState(false);
  const [currentProductDetail, setCurrentProductDetail] =
    useState<ProductDetails>({
      id: 0,
      name: '',
      price: 0,
      minType: '',
      minQuantity: 0,
      upchargeIngredientsFlag: 0,
      allowFatContentFlag: 0,
      productType: 1,
      weight: 0,
      allowAddOnFlag: 0,
    });
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    [],
  );
  const [productAddonListSheetData, setProductAddonListSheetData] = useState<
    ModifiedSelectedProduct[] | null
  >(null);

  /**
   * useEffect hook that initializes customer-related state on component mount.
   *
   * This effect runs once when the component mounts (empty dependency array).
   * - It retrieves the customer ID from the CustomerOrderService.
   * - If a customer ID exists:
   *   - Updates the local state with the customer ID.
   *   - Checks localStorage for a "currentCustomizingId" and updates local state if found.
   * - If no customer ID is found:
   *   - Redirects the user to the customer list page.
   *
   * Side effects:
   * - Reads from localStorage
   * - May cause a navigation using `navigate` function
   *
   * Dependencies: None (runs only once after the initial render)
   */
  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId();
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);

    if (customerID) {
      setCustomerId(customerID);
      const currentCustomizingId = localStorage.getItem('currentCustomizingId');
      if (currentCustomizingId) {
        setCurrentCustomizingId(currentCustomizingId);
      }
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  useEffect(() => {
    fetchMasterProducts();
  }, []);

  /**
   * Filters the master product list based on the user's search query.
   *
   * This effect is triggered whenever the search query or the full master product list changes.
   * It uses the `searchService` utility function to filter products whose `name` matches the query.
   * The filtered list is then stored in the `filteredProducts` state.
   *
   * @dependency searchKey - The current search input entered by the user.
   * @dependency masterProducts - The full list of master products to search within.
   *
   * @sideEffect Updates the filtered product list using setFilteredProducts().
   */
  useEffect(() => {
    const result = searchService(masterProducts, searchKey, 'name');
    setFilteredProducts(result);
  }, [searchKey, masterProducts]);

  /**
   * useEffect hook that triggers when `customerId` changes.
   *
   * When a valid `customerId` is available (non-null/non-undefined),
   * it calls `getOrderData()` to fetch or refresh order-related data
   * for the currently selected customer.
   *
   * This ensures that the component responds to any updates in the
   * customer ID by reloading the relevant order data.
   *
   * Dependencies:
   * - `customerId`: Runs the effect whenever `customerId` changes.
   */
  useEffect(() => {
    if (customerId) {
      getOrderData();
    }
  }, [customerId]);

  /**
   * Retrieves and sets the selected products for the current customer and customizing session.
   *
   * - Fetches parsed order data from `CustomerOrderService`.
   * - If valid `orderData` and `customerId` exist:
   *   - Looks up the customer's order using their ID.
   *   - Retrieves existing product data for the current customizing session (`currentCustomizingId`).
   *   - Updates the selected products state with the retrieved product list.
   *
   * Side Effects:
   * - Calls `setSelectedProducts` to update component state.
   *
   * Preconditions:
   * - Assumes `customerId` and `currentCustomizingId` are defined in the component scope.
   * - Assumes `CustomerOrderService.getParsedOrderData()` returns an object keyed by customer IDs.
   *
   * Returns:
   * - Nothing (`void`)
   */
  const getOrderData = (): void => {
    const orderData = CustomerOrderService.getParsedOrderData();
    if (orderData && customerId) {
      const customerOrder = orderData[customerId];
      const existingData = customerOrder?.species?.[currentCustomizingId];
      if (existingData.products) setSelectedProducts(existingData.products);
      else return;
    }
  };

  const handleAddCustomization = (customization: SelectedProduct) => {
    setSelectedProducts((prev) => [...prev, customization]);
  };

  /**
   * Removes a product customization from the selected products list at the specified index.
   *
   * @param {number} indexToRemove - The index of the product to remove from the selection.
   *
   * Behavior:
   * - Creates a shallow copy of the current `selectedProducts` array.
   * - Removes the item at `indexToRemove` using `splice`.
   * - Updates the state with the modified array via `setSelectedProducts`.
   *
   * Side Effects:
   * - Updates component state (`selectedProducts`) to reflect the removed item.
   *
   * Preconditions:
   * - `indexToRemove` must be a valid index in the `selectedProducts` array.
   */
  const handleRemoveCustomization = (indexToRemove: number): void => {
    setSelectedProducts((prev) => {
      const updated = [...prev];
      updated.splice(indexToRemove, 1);
      return updated;
    });
  };

  /**
   * Asynchronously fetches product master data from the server and updates local state.
   *
   * Makes a POST request to the `FETCH_SPECIES_MASTERDATA` API endpoint with the
   * specified master data type (`PRODUCT_MASTER_DATA`). On success:
   * - Updates both `masterProducts` and `filteredProducts` state with the fetched data.
   *
   * On failure:
   * - Displays an error toast with a predefined error message.
   * - Rethrows the error for potential upstream handling.
   *
   * @returns {Promise<void>} A promise that resolves when data fetching and state updates complete.
   *
   * Side Effects:
   * - Updates component state: `setMasterProducts` and `setFilteredProducts`.
   * - Displays toast notifications on error.
   *
   * Dependencies:
   * - Axios
   * - ApiEndpoints
   * - OrderCreationMasterData
   * - showToastMessage (for error feedback)
   */
  const fetchMasterProducts = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PRODUCT_MASTER_DATA,
      });
      setMasterProducts(response.data.data.masterData);
      setFilteredProducts(response.data.data.masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const getSelectedProduct = (productId: number) =>
    selectedProducts.find((p) => p.productId === productId);

  /**
   * Calculates the total quantity of a specific product across all selected products.
   *
   * @param {number} productId - The ID of the product for which the total quantity should be calculated.
   * @returns {number} The total quantity of the specified product.
   *
   * Behavior:
   * - Filters the `selectedProducts` array to include only products matching the given `productId`.
   * - Sums up the `quantity` of each matching product using `reduce`.
   */
  const getTotalQuantity = (productId: number) =>
    selectedProducts
      .filter((product) => product.productId === productId)
      .reduce((total, product) => total + Number(product.quantity), 0);

  /**
   * Handles the addition of a new product customization from the Add-on selection sheet.
   *
   * @param {Object} payload - The customization data to be added to the selected products.
   * @param {number} payload.productId - The ID of the selected product.
   * @param {number} payload.quantity - The quantity of the product selected.
   * @param {number} payload.fatPercent - The fat percentage for the selected product.
   * @param {number[]} payload.addOnIds - An array of selected add-on IDs associated with the product.
   *
   * Behavior:
   * - Appends the new payload object to the existing `selectedProducts` array.
   * - Closes the add-on selection sheet by setting `setAddonSelectionSheet(false)`.
   */
  const handleAddonSheetAdd = (payload: {
    productId: number;
    quantity: number | string;
    fatPercent: number;
    addOnIds: number[];
  }) => {
    setSelectedProducts((prev) => {
      return [...prev, payload];
    });
    setAddonSelectionSheet(false);
  };

  /**
   * Handles both increment and decrement button actions for a product.
   *
   * @param {ProductDetails} product - The product whose customization list is to be shown.
   * @param {boolean} isDeletion - Flag to indicate whether the operation is a decrement (true) or increment (false).
   */
  const handleProductCustomization = (
    product: ProductDetails,
    isDeletion: boolean,
  ): void => {
    setDeletion(isDeletion);

    const selectedCustomizations = selectedProducts
      .map((p, i) => ({ ...p, index: i }))
      .filter((p) => p.productId === product.id);

    if (selectedCustomizations.length > 0) {
      setCurrentProductDetail({
        id: product.id,
        name: product.name,
        price: product.price,
        minType: product.minType,
        minQuantity: product.minQuantity,
        upchargeIngredientsFlag: product.upchargeIngredientsFlag,
        allowFatContentFlag: product.allowFatContentFlag,
        productType: product.productType,
        weight: product.weight,
        allowAddOnFlag: product.allowAddOnFlag
      });
      setProductAddonListSheetData(selectedCustomizations);
      setProductAddonListSheet(true);
    }
  };

  /**
   * Handles the "Add" action for a product.
   *
   * Opens the add-on selection sheet and sets the current product details
   * based on the provided `product` information.
   *
   * @param {ProductDetails} product - The product to be added, used to populate the customization sheet.
   * @returns {void}
   *
   */
  const handleAdd = (product: ProductDetails): void => {
    setAddonSelectionSheet(true);
    setCurrentProductDetail({
      id: product.id,
      name: product.name,
      price: product.price,
      minType: product.minType,
      minQuantity: product.minQuantity,
      upchargeIngredientsFlag: product.upchargeIngredientsFlag,
      allowFatContentFlag: product.allowFatContentFlag,
      productType: product.productType,
      weight: product.weight,
      allowAddOnFlag: product.allowAddOnFlag
    });
  };

  /**
   * Handles the "Next" action in the order creation flow.
   *
   * Validates the presence of selected products before proceeding. If valid:
   * - Retrieves the current parsed order data from `CustomerOrderService`.
   * - Ensures the `species` array exists for the customer.
   * - Ensures the `species` array has an entry for the current customizing ID.
   * - Updates the appropriate species entry with the selected products.
   * - Saves the updated order data to localStorage.
   * - Displays a success toast message.
   * - Navigates to the product details creation route.
   *
   * If no products are selected:
   * - Displays an error toast and exits early.
   *
   * @returns {void}
   *
   * Side Effects:
   * - Reads from and writes to localStorage (`orderData`).
   * - Updates toast messages using `showToastMessage`.
   * - Navigates to another route using `navigate`.
   *
   * Dependencies:
   * - `CustomerOrderService.getParsedOrderData()`
   * - `showToastMessage`
   * - `navigate`
   * - `FrontendRoutes`
   * - `selectedProducts`, `customerId`, `currentCustomizingId`
   */
  const handleNext = (): void => {
    if (!selectedProducts || selectedProducts.length === 0) {
      showToastMessage(errorMessage.ORDER_PRODUCT_REQUIRED, 'error');
      return;
    }
    const orderDataPayload = CustomerOrderService.getParsedOrderData();
    orderDataPayload[customerId].species ??= [];
    while (
      orderDataPayload[customerId].species.length <= currentCustomizingId
    ) {
      orderDataPayload[customerId].species.push({ products: [] });
    }
    const existingSpecies =
      orderDataPayload[customerId].species[currentCustomizingId] ?? {};
    orderDataPayload[customerId].species[currentCustomizingId] = {
      ...existingSpecies,
      products: selectedProducts,
    };
    showToastMessage(responseTexts.PRODUCT_CREATE_SUCCESS, 'success');
    localStorage.setItem('orderData', JSON.stringify(orderDataPayload));

    navigate(FrontendRoutes.CREATE_ORDER_PRODUCT_DETAILS);
  };

  return (
    <div className="flex h-screen w-screen">
      {/* Sidebar */}
      <SideNav active={2} />
      {/* Main Content */}
      <div className="flex-1 overflow-auto relative">
        <div>
          {/* Header */}
          <ButcherHeader />
          {/* Breadcrumbs */}
          <OrderBreadcrumb
            steps={orderBreadCrumbSteps}
            customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
            activeItem="New Order"
            customerId={customerId}
          />
        </div>
        <div className="p-3">
          {/* Customer Profile Card */}
          <OrderProfileCard />
          {/* Stepper */}
          <OrderStepper steps={orderStepper} currentStep={3} />
          <div>
            <div className="space-y-2">
              <div className="flex justify-between mt-4">
                <h2 className="text-lg font-medium mb-2">Products</h2>
                <div className="relative">
                  <input
                    type="text"
                    value={searchKey}
                    onChange={(e) => setsearchKey(e.target.value)}
                    placeholder="Search..."
                    className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pb-12">
                {filteredProducts.map((product) => {
                  const selected = getSelectedProduct(product.id);
                  return (
                    <div key={product.id}>
                      <div
                        className={`border p-4 rounded-lg shadow-md  ${selected ? 'bg-tertiary border-primary' : ''}`}
                      >
                        <div className="flex justify-between font-semibold text-md text-[#1C1B1F]">
                          <div>{product.name}</div>
                          <div>
                            {/* Display Item as mintype for product type quantity and lbs for weight */}
                            ${product.price}/
                            {product.productType === ProductType.QUANTITY
                              ? 'Item'
                              : `${WeightType.LBS}`}
                          </div>
                        </div>
                        <div className="mt-12">
                          <span className="text-sm text-gray-600 mb-3 font-semibold">
                            <span className="text-sm text-gray-600 mb-3 font-semibold">
                              {product.productType === ProductType.QUANTITY
                                ? `Min Quantity: ${product.minQuantity}`
                                : `Min Weight: ${product.weight} ${WeightType.LBS}`}
                            </span>
                          </span>
                        </div>
                      </div>
                      {selected ? (
                        <div className="flex items-center w-full mt-4">
                          <button
                            onClick={() =>
                              handleProductCustomization(product, true)
                            }
                            type="button"
                            className="w-12 h-10 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                          >
                            -
                          </button>
                          <input
                            type="text"
                            value={getTotalQuantity(selected.productId)}
                            className="flex-1 h-10 text-center border-t border-b border-gray-400"
                            readOnly
                          />
                          <button
                            onClick={() =>
                              handleProductCustomization(product, false)
                            }
                            type="button"
                            className="w-12 h-10 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center w-full mt-4">
                          <button
                            onClick={() => handleAdd(product)}
                            type="button"
                            className="w-full px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {addonSelectionSheet && (
              <AddonSelectionSheet
                onClose={() => setAddonSelectionSheet(false)}
                onAdd={handleAddonSheetAdd}
                productDetails={currentProductDetail}
              />
            )}
            {productAddonListSheet && productAddonListSheetData && (
              <ProductAddonListSheet
                isDeletionMode={deletion}
                productDetails={currentProductDetail}
                addonData={productAddonListSheetData}
                onClose={() => {
                  setProductAddonListSheet(false);
                }}
                onAddCustomization={handleAddCustomization}
                onDeleteCustomization={handleRemoveCustomization}
                showAddonSelectionSheet={setAddonSelectionSheet}
              />
            )}
          </div>
        </div>
        <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4">
          <button
            onClick={() => navigate(FrontendRoutes.CREATE_ORDER_SERVICE)}
            type="button"
            className="w-[50%] px-5 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            type="submit"
            className="w-[50%] px-5 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
          >
            <span>Next</span>
          </button>
        </div>
      </div>
    </div>
  );
};
